// 全局常量定义

// 应用配置
export const APP_CONFIG = {
  NAME: '大悟万能墙',
  VERSION: '2.0.0',
  DESCRIPTION: '大悟县校园社交平台',
  KEYWORDS: ['校园', '社交', '大悟', '学生'],
  AUTHOR: 'DaWu Team',
  COPYRIGHT: '© 2024 大悟万能墙. All rights reserved.'
} as const

// 路由路径
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  PROFILE: '/profile',
  USER_PROFILE: '/user/:id',
  PUBLISH: '/publish',
  SEARCH: '/search',
  CATEGORY: '/category/:category',
  MESSAGE: '/message',
  TRENDING: '/trending',
  PRIVACY: '/privacy',
  TERMS: '/terms'
} as const

// API端点
export const API_ENDPOINTS = {
  // 认证
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  LOGOUT: '/auth/logout',
  REFRESH_TOKEN: '/auth/refresh',
  
  // 用户
  USER_PROFILE: '/users/profile',
  UPDATE_PROFILE: '/users/update',
  UPLOAD_AVATAR: '/users/avatar',
  
  // 帖子
  POSTS: '/posts',
  POST_DETAIL: '/posts/:id',
  CREATE_POST: '/posts',
  UPDATE_POST: '/posts/:id',
  DELETE_POST: '/posts/:id',
  LIKE_POST: '/posts/:id/like',
  
  // 评论
  COMMENTS: '/comments',
  CREATE_COMMENT: '/comments',
  DELETE_COMMENT: '/comments/:id',
  
  // 消息
  CONVERSATIONS: '/messages/conversations',
  SEND_MESSAGE: '/messages/send',
  
  // 搜索
  SEARCH_POSTS: '/search/posts',
  SEARCH_USERS: '/search/users',
  
  // 文件上传
  UPLOAD_IMAGE: '/upload/image'
} as const

// 本地存储键名
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_INFO: 'user_info',
  THEME: 'theme',
  LANGUAGE: 'language',
  SEARCH_HISTORY: 'search_history',
  DRAFT_POST: 'draft_post'
} as const

// 主题配置
export const THEME_CONFIG = {
  COLORS: {
    PRIMARY: '#3B82F6',
    SECONDARY: '#F59E0B',
    SUCCESS: '#10B981',
    WARNING: '#F59E0B',
    ERROR: '#EF4444',
    INFO: '#3B82F6'
  },
  BREAKPOINTS: {
    SM: 640,
    MD: 768,
    LG: 1024,
    XL: 1280,
    '2XL': 1536
  }
} as const

// 分页配置
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100]
} as const

// 文件上传配置
export const UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_IMAGE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_FILE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
  MAX_IMAGES_PER_POST: 9
} as const

// 表单验证规则
export const VALIDATION_RULES = {
  USERNAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 20,
    PATTERN: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/
  },
  PASSWORD: {
    MIN_LENGTH: 6,
    MAX_LENGTH: 50,
    PATTERN: /^(?=.*[a-zA-Z])(?=.*\d).{6,}$/
  },
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  },
  POST_CONTENT: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 2000
  },
  COMMENT_CONTENT: {
    MIN_LENGTH: 1,
    MAX_LENGTH: 500
  }
} as const

// 帖子分类（与后端 Post.category enum 保持一致，发布/筛选/展示共用）
export const POST_CATEGORIES = [
  { id: 'study', name: '学习', icon: '📚', color: '#10B981' },
  { id: 'life', name: '生活', icon: '🏫', color: '#3B82F6' },
  { id: 'entertainment', name: '娱乐', icon: '🎮', color: '#F59E0B' },
  { id: 'help', name: '求助', icon: '🤝', color: '#EF4444' },
  { id: 'trade', name: '交易', icon: '🛒', color: '#8B5CF6' },
  { id: 'other', name: '其他', icon: '📝', color: '#6B7280' }
] as const

// 学校列表
export const SCHOOLS = [
  { id: 'dawu_high', name: '大悟县第一中学', shortName: '大悟一中' },
  { id: 'dawu_vocational', name: '大悟县职业技术学校', shortName: '大悟职校' },
  { id: 'dawu_experimental', name: '大悟县实验中学', shortName: '实验中学' },
  { id: 'other', name: '其他学校', shortName: '其他' }
] as const

// 时间格式
export const DATE_FORMATS = {
  FULL: 'YYYY-MM-DD HH:mm:ss',
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'MM-DD HH:mm',
  RELATIVE: 'relative'
} as const

// 错误消息
export const ERROR_MESSAGES = {
  NETWORK_ERROR: '网络连接失败，请检查网络设置',
  SERVER_ERROR: '服务器错误，请稍后重试',
  UNAUTHORIZED: '登录已过期，请重新登录',
  FORBIDDEN: '权限不足，无法执行此操作',
  NOT_FOUND: '请求的资源不存在',
  VALIDATION_ERROR: '输入信息有误，请检查后重试',
  UPLOAD_ERROR: '文件上传失败，请重试',
  UNKNOWN_ERROR: '未知错误，请联系管理员'
} as const

// 成功消息
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: '登录成功',
  REGISTER_SUCCESS: '注册成功',
  POST_CREATED: '发布成功',
  POST_UPDATED: '更新成功',
  POST_DELETED: '删除成功',
  COMMENT_CREATED: '评论成功',
  LIKE_SUCCESS: '点赞成功',
  UNLIKE_SUCCESS: '取消点赞',
  FOLLOW_SUCCESS: '关注成功',
  UNFOLLOW_SUCCESS: '取消关注',
  UPLOAD_SUCCESS: '上传成功'
} as const

// 正则表达式
export const REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^1[3-9]\d{9}$/,
  USERNAME: /^[a-zA-Z0-9_\u4e00-\u9fa5]{2,20}$/,
  PASSWORD: /^(?=.*[a-zA-Z])(?=.*\d).{6,50}$/,
  URL: /^https?:\/\/.+/,
  HASHTAG: /#[\u4e00-\u9fa5a-zA-Z0-9_]+/g,
  MENTION: /@[\u4e00-\u9fa5a-zA-Z0-9_]+/g
} as const

// 动画配置
export const ANIMATION_CONFIG = {
  DURATION: {
    FAST: 150,
    NORMAL: 300,
    SLOW: 500
  },
  EASING: {
    EASE_IN: 'ease-in',
    EASE_OUT: 'ease-out',
    EASE_IN_OUT: 'ease-in-out'
  }
} as const

// 缓存配置
export const CACHE_CONFIG = {
  TTL: {
    SHORT: 5 * 60 * 1000, // 5分钟
    MEDIUM: 30 * 60 * 1000, // 30分钟
    LONG: 24 * 60 * 60 * 1000 // 24小时
  },
  KEYS: {
    USER_PROFILE: 'user_profile',
    POST_LIST: 'post_list',
    CATEGORIES: 'categories',
    SCHOOLS: 'schools'
  }
} as const